import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  CircularProgress,
  Box,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query'; //-tech-debt-react-query;
import { postItem, putItem } from '../../../support/queries';
import { CacheKey } from '../../../types/cache-keys';

export type EscalationAssignmentType = {
  id?: number;
  name: string;
  description?: string | null;
  level: number;
};

interface Props {
  open: boolean;
  editItem: EscalationAssignmentType | null;
  onClose: () => void;
  readOnly?: boolean;
}

const EscalationAssignmentTypeEditDialog: React.FC<Props> = ({
                                                               open,
                                                               editItem,
                                                               onClose,
                                                               readOnly = false,
                                                             }) => {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [level, setLevel] = useState<number>(1);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setName(editItem?.name ?? '');
    setDescription(editItem?.description ?? '');
    setLevel(editItem?.level ?? 1);
  }, [editItem, open]);

  const handleSave = async () => {
    if (!name.trim()) {
      alert('Name is required.');
      return;
    }

    if (!Number.isInteger(level) || level <= 0) {
      alert('Level must be a positive integer.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        id: editItem?.id,
        name,
        description,
        level,
      };

      if (editItem?.id) {
        await putItem('db/escalation-assignment-type', payload);
      } else {
        await postItem('db/escalation-assignment-type', payload);
      }

      queryClient.invalidateQueries({ queryKey: [CacheKey.Admin, 'escalationAssignmentTypes'] });
      onClose();
    } catch {
      alert('Failed to save escalation assignment type.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {editItem?.id ? 'Edit Escalation Assignment Type' : 'Add Escalation Assignment Type'}
      </DialogTitle>
      <DialogContent>
        <TextField
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          required
          margin="normal"
          disabled={readOnly || saving}
        />
        <TextField
          label="Level"
          type="number"
          value={level}
          onChange={(e) => setLevel(Number(e.target.value))}
          fullWidth
          required
          margin="normal"
          disabled={readOnly || saving}
        />
        <TextField
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          multiline
          minRows={3}
          margin="normal"
          disabled={readOnly || saving}
        />
        {saving && (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={24} />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {!readOnly && (
          <Button onClick={handleSave} variant="contained" disabled={saving || !name.trim()}>
            Save
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EscalationAssignmentTypeEditDialog;